import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

/** @type {import('../types/auth').AuthContextType} */
const defaultAuth = {
  user: null,
  roleLabel: '',
  loading: true,
  isAuthenticated: false,
  login: async () => ({}),
  logout: () => {},
  refreshUser: async () => {}
};

const AuthContext = createContext(defaultAuth);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(defaultAuth.user);
  const [roleLabel, setRoleLabel] = useState(defaultAuth.roleLabel);
  const [loading, setLoading] = useState(defaultAuth.loading);

  useEffect(() => {
    const initAuth = async () => {
      const stored = authService.getStoredAuth();
      if (stored?.token) {
        try {
          const fresh = await authService.getMe();
          if (fresh) {
            setUser(fresh.user);
            setRoleLabel(fresh.roleLabel || '');
          } else {
            setUser(null);
            setRoleLabel('');
          }
        } catch {
          setUser(null);
          setRoleLabel('');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const authData = await authService.login(email, password);
    setUser(authData.user);
    setRoleLabel(authData.roleLabel || '');
    return authData;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setRoleLabel('');
  };

  const refreshUser = async () => {
    const fresh = await authService.getMe();
    if (fresh) {
      setUser(fresh.user);
      setRoleLabel(fresh.roleLabel || '');
    }
  };

  const value = {
    user,
    roleLabel,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
